/*
 * Jobs: the list, one job's page (details, line items, crew notes in English and Spanish, visits, notes) and the job form
 * (Repeats choice, line items with their own repeat, crew, crew notes, and "Starting when" for new prices on a repeating job).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  addDays,
  billingWords as billingLabel,
  FREQUENCIES,
  firstRepeat,
  homeSize,
  homeWords,
  moveRepeat,
  nextAfterSwitch,
  PRICE_OFTEN,
  PRICE_OFTEN_WORDS,
  priceMath,
  type HomeAnswers,
  type JobBilling as BillingKind,
  type JobDetail,
  type JobPatch,
  type JobRow,
  type LineInput,
  type LineRow,
  type PriceList,
  type PropertyRow,
  type QuickQuote,
  type QuoteDetail,
  type RepeatValue,
  type VisitCard,
} from "@steward/shared";
import { useState, type SubmitEvent } from "react";
import {
  useChecklists,
  useClient,
  useClients,
  useCreateJob,
  useJob,
  useJobCosts,
  useJobPages,
  useJobs,
  usePeople,
  useSaveProperty,
  useSettings,
  useUpdateJob,
  useVisitFix,
  useVisitLines,
  useVisits,
  type JobsQuery,
} from "../api/hooks";
import { useSession } from "../app/session";
import { DataTable, MoreRows } from "../components/DataTable";
import { Choice, Combobox, FormField, InputCount, InputText } from "../components/fields";
import {
  clock,
  daysAway,
  dayWords,
  minutesWords,
  money,
  moneyExact,
  phoneText,
  shortDate,
  timeRange,
  todayIn,
} from "../components/format";
import { Icon } from "../components/Icon";
import { PhoneRecord } from "../components/PhoneRecord";
import { PlanOfferSend } from "../components/PlanOffer";
import { usePhone } from "../components/usePhone";
import { useInvoices, usePayments, useQuote } from "../api/money";
import { InvoiceStatus } from "./invoices/common";
import { cardLine, JobBilling, JobExpenses, JobLabor, JobProfit, JobTimeLog } from "./JobCosts";
import { PhoneCard } from "./PhoneCard";
import { markFresh, NextStep } from "../components/next";
import { Menu, Modal, useToast } from "../components/overlays";
import {
  Banner,
  Button,
  Card,
  Chip,
  Details,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  Skeleton,
  StatusLabel,
  Switch,
} from "../components/parts";
import {
  CrewChips,
  draftLine,
  errText,
  LinesEditor,
  LineMore,
  LinesTable,
  linesOut,
  NotesCard,
  VisitStatus,
  type DraftLine,
} from "../components/work";
import { LawnQuickPrice, QuickPrice } from "./QuickPrice";
import { RepeatPicker } from "../components/repeat";
import { NEW_CLIENT, NewClientBox, type NewClientStart } from "./quotes/Form";

export function JobsList() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState<JobsQuery>({ status: "active", kind: "all" });
  const [typed, setTyped] = useState("");
  const jobs = useJobPages(company.id, { ...q, q: typed.trim() || undefined });
  const all = useJobs(company.id, { status: "all" });
  const rows = jobs.data?.pages.flatMap((p) => p.items) ?? [];
  const moreToLoad = jobs.hasNextPage;
  const loadMore = () => {
    if (jobs.hasNextPage && !jobs.isFetchingNextPage) void jobs.fetchNextPage();
  };
  const every = all.data?.items ?? [];
  const today = todayIn(company.timezone);
  const active = every.filter((j) => j.active);
  return (
    <Page
      title="Jobs"
      primaryAction={
        can("job.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/jobs/new", search: {} })}>
            New job
          </Button>
        ) : null
      }
    >
      <div className="tiles">
        <div className="tile">
          <span className="label">Active jobs</span>
          <span className="value">{all.data ? active.length : <span className="skeleton inline" />}</span>
          <span className="hint">{active.filter((j) => j.frequency !== "one-time").length} repeat</span>
        </div>
        <div className="tile">
          <span className="label">Value per visit round</span>
          <span className="value">{money(active.reduce((s, j) => s + j.visitTotalCents, 0))}</span>
          <span className="hint">Every active job once</span>
        </div>
        <div className="tile">
          <span className="label">No crew yet</span>
          <span className="value">{active.filter((j) => !j.crew.length).length}</span>
          <span className="hint">Active jobs nobody is on</span>
        </div>
        <div className="tile">
          <span className="label">Next 7 days</span>
          <span className="value">{active.filter((j) => j.nextVisit && j.nextVisit <= addDays(today, 6)).length}</span>
          <span className="hint">Jobs with a visit coming</span>
        </div>
      </div>
      <DataTable<JobRow>
        title="All jobs"
        rows={rows}
        rowKey={(j) => j.id}
        loading={jobs.isFetching && !jobs.isFetchingNextPage}
        error={jobs.error ? <LoadError error={jobs.error} retry={() => void jobs.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Job, client or number" }}
        onRowClick={(j) => void navigate({ to: "/jobs/$id", params: { id: j.id } })}
        count={moreToLoad ? `${String(rows.length)} so far` : undefined}
        footer={moreToLoad ? <MoreRows loading={jobs.isFetchingNextPage} onMore={loadMore} /> : null}
        tools={
          <div className="chips">
            {(
              [
                ["active", "Active"],
                ["ended", "Ended"],
                ["all", "All"],
              ] as const
            ).map(([v, label]) => (
              <Chip key={v} pressed={q.status === v} onClick={() => setQ({ ...q, status: v })}>
                {label}
              </Chip>
            ))}
            <Chip
              pressed={q.kind === "repeating"}
              onClick={() => setQ({ ...q, kind: q.kind === "repeating" ? "all" : "repeating" })}
            >
              Repeating
            </Chip>
            <Chip
              pressed={q.kind === "one-time"}
              onClick={() => setQ({ ...q, kind: q.kind === "one-time" ? "all" : "one-time" })}
            >
              One time
            </Chip>
          </div>
        }
        empty={
          <EmptyState icon="jobs" title="No jobs yet">
            A job is the work for a client. Repeating jobs fill the schedule on their own.
          </EmptyState>
        }
        rowClass={(j) => (j.active ? undefined : "inactive")}
        columns={[
          {
            key: "number",
            header: "#",
            sort: (j) => j.number ?? 0,
            render: (j) => <span className="num muted">{j.number}</span>,
          },
          {
            key: "name",
            header: "Job",
            sort: (j) => j.clientName,
            render: (j) => (
              <div className="name-cell">
                <b>{j.clientName}</b>
                <span className="muted">{j.name}</span>
              </div>
            ),
          },
          { key: "often", header: "Repeats", render: (j) => j.frequencyLabel },
          {
            key: "next",
            header: "Next visit",
            sort: (j) => j.nextVisit ?? "9999",
            render: (j) =>
              j.completedOn ? (
                <StatusLabel status="success">Completed {shortDate(j.completedOn)}</StatusLabel>
              ) : j.active ? (
                j.nextVisit ? (
                  <span>
                    {shortDate(j.nextVisit)} <span className="muted">{clock(j.startTime, "")}</span>
                  </span>
                ) : (
                  <span className="muted">None</span>
                )
              ) : (
                <StatusLabel status="inactive">Ended {shortDate(j.endsOn)}</StatusLabel>
              ),
          },
          { key: "crew", header: "Crew", render: (j) => <CrewChips crew={j.crew} /> },
          {
            key: "value",
            header: "Per visit",
            align: "right",
            sort: (j) => j.visitTotalCents,
            render: (j) => <span className="num">{money(j.visitTotalCents)}</span>,
          },
        ]}
      />
    </Page>
  );
}

// One job ------------------------------------------------------------------------------------------------------------

export function JobPage({ id }: { id: string }) {
  const { company, can, me } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const job = useJob(id);
  const update = useUpdateJob();
  const prices = useSettings(company.id).data?.settings.prices;
  const [repricing, setRepricing] = useState<LineRow | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [lang, setLang] = useState<"en" | "es">(me.locale === "es" ? "es" : "en");
  const phone = usePhone();
  // The phone page has Call, Text and Email up top, from the client. Both pages show the home's size over the line items.
  const client = useClient(job.data?.clientId ?? "", !!job.data && can("client.read"));
  // The phone page's Invoices and Payments, like Jobber's job.
  const [jobTab, setJobTab] = useState<"job" | "notes">("job");
  const onPhoneBills = phone && !!job.data && can("invoice.read");
  const jobInvoices = useInvoices(company.id, { state: "all", jobId: job.data?.id ?? "" }, onPhoneBills);
  const jobPayments = usePayments(company.id, { jobId: job.data?.id ?? "" }, onPhoneBills);
  // Expenses, Labor and Profit (Labor and Profit come back only for people who see payroll).
  const costs = useJobCosts(job.data?.id ?? "", !!job.data && can("job.read"));
  const j = job.data;
  if (job.error)
    return (
      <Page title="Job">
        <LoadError error={job.error} retry={() => void job.refetch()} />
      </Page>
    );
  if (!j)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("job.update");
  const repeating = j.frequency !== "one-time";
  const setActive = (active: boolean) =>
    update.mutate(
      { id: j.id, patch: { active } },
      {
        onSuccess: () =>
          toast(active ? "Job started again" : "Job ended. No more visits get planned.", {
            undo: () => update.mutate({ id: j.id, patch: { active: !active } }),
          }),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  const status = j.confirm ? (
    <StatusLabel status="warning">Not confirmed</StatusLabel>
  ) : j.waitingOk ? (
    <StatusLabel status="warning">Waiting for your OK</StatusLabel>
  ) : j.completedOn ? (
    <StatusLabel status="success">Completed {shortDate(j.completedOn)}</StatusLabel>
  ) : j.active ? null : (
    <StatusLabel status="inactive">Ended {shortDate(j.endsOn)}</StatusLabel>
  );
  const editBtn = editable ? (
    <Button icon="edit" onClick={() => void navigate({ to: "/jobs/$id/edit", params: { id: j.id } })}>
      Edit job
    </Button>
  ) : null;
  const scheduleBtn = can("schedule.read") ? (
    <Button type="secondary" icon="schedule" onClick={() => void navigate({ to: "/schedule" })}>
      Schedule
    </Button>
  ) : null;
  const moreMenu = editable ? (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(close) => (
        <>
          {j.confirm ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                setConfirming(true);
              }}
            >
              Confirm for them
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              setActive(!j.active);
            }}
          >
            {j.active ? (repeating ? "End this job" : "Cancel this job") : "Start this job again"}
          </button>
        </>
      )}
    </Menu>
  ) : null;
  const banners = (
    <>
      <NextStep freshKey={`job:${j.id}`} title="The job is made. Its visits are on the schedule.">
        <Button type="secondary" size="small" icon="schedule" onClick={() => void navigate({ to: "/schedule" })}>
          Open the schedule
        </Button>
      </NextStep>
      {j.confirm ? (
        <Banner
          type="notice"
          action={
            <div className="row">
              <Button
                type="secondary"
                size="small"
                onClick={() => void navigate({ to: "/quotes/$id", params: { id: j.confirm?.quoteId ?? "" } })}
              >
                {j.confirm.sentTo ? "See the link" : "Send the link"}
              </Button>
              {editable ? (
                <Button size="small" onClick={() => setConfirming(true)}>
                  Confirm for them
                </Button>
              ) : null}
            </div>
          }
        >
          <b>Not confirmed yet.</b>{" "}
          {j.confirm.sentTo
            ? `It is on the schedule. ${j.clientName} got a link at ${j.confirm.sentTo} to confirm and sign.`
            : `It is on the schedule. ${j.clientName} has no phone or email. Send the confirm link from the quote.`}
        </Banner>
      ) : j.waitingOk ? (
        <Banner
          type="warning"
          action={
            editable ? (
              <Button size="small" onClick={() => setActive(true)}>
                Book it
              </Button>
            ) : null
          }
        >
          <b>The client asked for this visit in My Trades.</b> It is not on the schedule yet. {j.waitingOk.join(" ")}
        </Banner>
      ) : null}
      {j.officeNotes ? (
        <Banner type="notice">
          <b>Office note.</b> {j.officeNotes}
        </Banner>
      ) : null}
      {confirming && j.confirm ? <ConfirmForThem job={j} onClose={() => setConfirming(false)} /> : null}
    </>
  );
  // Home cleaning only for now (owner, 2026-11-02: businesses wait until it is refined).
  const homeClean = company.trade === "cleaning" && client.data?.kind === "home";
  const place = client.data?.properties.find((p) => p.id === j.propertyId) ?? null;
  // A job not priced from the price list prices its first line from the home's size, to offer a repeat fast.
  const unpriced = !j.lines.some((l) => l.answers);
  const canPrice = editable && Boolean(prices?.services.length);
  const linesCard = (
    <>
      <Card title="Line items" flush>
        {homeClean ? (
          <JobHome
            job={j}
            prices={prices}
            place={place}
            clientId={j.clientId}
            priced={j.lines.find((l) => l.basis)?.basis ?? null}
            editable={editable && can("client.update")}
            onPrice={canPrice && unpriced && j.lines[0] ? () => setRepricing(j.lines[0] ?? null) : null}
            offer={!repeating && can("quote.update")}
          />
        ) : null}
        <LinesTable
          lines={j.lines}
          footer={<span className="muted">Each visit</span>}
          onOpen={(l) =>
            canPrice && (l.answers || (homeClean && unpriced && l.id === j.lines[0]?.id)) ? () => setRepricing(l) : null
          }
          below={(l) => (
            <>
              <PricedNote line={l} list={prices} />
              <LineMore line={l} basis={!(l.answers && prices)} />
            </>
          )}
        />
      </Card>
      {repricing && prices ? (
        <RepriceLine job={j} line={repricing} list={prices} place={place} onClose={() => setRepricing(null)} />
      ) : null}
    </>
  );
  const visitsCard = <JobVisits job={j} today={todayIn(company.timezone)} />;
  const expensesCard = <JobExpenses j={j} costs={costs} />;
  const laborCard = costs.data?.labor ? <JobLabor costs={costs.data} /> : null;
  const timeCard = <JobTimeLog j={j} costs={costs} />;
  const profitCard = costs.data?.profit ? <JobProfit costs={costs.data} /> : null;
  const notesCard = <NotesCard on="jobs" id={j.id} notes={j.notesList} editable={editable} />;
  const detailsCard = (
    <Card title="Details">
      <Details
        rows={[
          // A repeating job shows how it repeats, its first and last visit and the checklist on Scheduled visits.
          ...(repeating
            ? []
            : ([
                ["Repeats", j.frequencyLabel],
                ["Date", shortDate(j.startDate)],
              ] as [string, string][])),
          ["Time", j.arriveFrom ? `Arrive ${timeRange(j.arriveFrom, j.arriveTo)}` : clock(j.startTime)],
          ["Takes", j.hours ? minutesWords(j.hours * 60) : null],
          ["Where", j.address],
          ["Crew", <CrewChips key="crew" crew={j.crew} />],
          ["Billing", billingLabel(j.billing)],
          ...(repeating ? [] : ([["Checklist", j.checklist?.name ?? null]] as [string, string | null][])),
        ]}
      />
    </Card>
  );
  const crewNotesCard = (
    <Card
      title="Crew notes"
      action={
        j.crewNotesEs ? (
          <div className="chips">
            <Chip pressed={lang === "en"} onClick={() => setLang("en")}>
              English
            </Chip>
            <Chip pressed={lang === "es"} onClick={() => setLang("es")}>
              Español
            </Chip>
          </div>
        ) : null
      }
    >
      {(lang === "es" ? j.crewNotesEs : j.crewNotes) ?? <span className="muted">No notes for the crew.</span>}
      {j.crewNotes && !j.crewNotesEs ? (
        <p className="muted small">No Spanish notes yet. Spanish speakers see the English.</p>
      ) : null}
    </Card>
  );

  if (phone) {
    // Like Jobber's job on a phone (owner shots, 2026-10-30): Job #14 for the client for the total, the job's name, the
    // address, the start, Go to Visits, then Job and Notes tabs. The Job tab runs Instructions, Line items, totals,
    // Schedule, Visits, Overview, Invoicing, Invoices and Payments, flush with lines between.
    const totalCents = j.lines.reduce((s, l) => s + l.amountCents, 0);
    const goVisits = () => {
      setJobTab("job");
      requestAnimationFrame(() => document.getElementById("job-visits")?.scrollIntoView({ behavior: "smooth" }));
    };
    const phoneMore = (
      <Menu align="left" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
        {(close) => (
          <>
            {editable && j.confirm ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setConfirming(true);
                }}
              >
                Confirm for them
              </button>
            ) : null}
            {editable ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => (close(), void navigate({ to: "/jobs/$id/edit", params: { id: j.id } }))}
              >
                Edit job
              </button>
            ) : null}
            {can("schedule.read") ? (
              <button type="button" role="menuitem" onClick={() => (close(), void navigate({ to: "/schedule" }))}>
                Open the schedule
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => (close(), void navigate({ to: "/clients/$id", params: { id: j.clientId } }))}
            >
              Open client
            </button>
            {editable ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setActive(!j.active);
                }}
              >
                {j.active ? (repeating ? "End this job" : "Cancel this job") : "Start this job again"}
              </button>
            ) : null}
          </>
        )}
      </Menu>
    );
    const invoiceRows = (jobInvoices.data?.invoices ?? []).filter((i) => i.state !== "to_check");
    const paymentRows = (jobPayments.data?.payments ?? []).filter((p) => p.status !== "failed");
    const billingWords = j.billing === "visit" ? "When a visit is marked complete" : billingLabel(j.billing);
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/jobs" }) }}
        kind="hammer"
        status={status ?? <StatusLabel status="success">Active</StatusLabel>}
        title={
          <>
            Job {j.number ? `#${String(j.number)} ` : ""}for {j.clientName}
            {totalCents ? ` for ${moneyExact(totalCents)}` : ""}
          </>
        }
        sub={<span className="prec-sub-muted">{j.name}</span>}
        phone={client.data?.phone ?? null}
        email={client.data?.email ?? null}
        facts={[
          ...(j.address
            ? [{ key: "where", icon: "directions" as const, main: j.address, sub: "Directions", directions: j.address }]
            : []),
          {
            key: "start",
            icon: "schedule" as const,
            main: j.startDate ? `${repeating ? "Start date" : "Date"} ${shortDate(j.startDate)}` : "No start date",
            sub: j.nextVisit ? `Next visit ${shortDate(j.nextVisit)}` : j.frequencyLabel,
          },
        ]}
        actions={
          <>
            <Button onClick={goVisits}>Go to Visits</Button>
            {phoneMore}
          </>
        }
        tabs={{
          tabs: [
            { key: "job", label: "Job" },
            { key: "notes", label: "Notes" },
          ],
          active: jobTab,
          onChange: (k) => setJobTab(k as typeof jobTab),
        }}
      >
        {banners}
        {jobTab === "notes" ? (
          notesCard
        ) : (
          <>
            {crewNotesCard}
            {linesCard}
            <section className="card prec-totals">
              <div>
                <span>Subtotal</span>
                <b className="num">{moneyExact(totalCents)}</b>
              </div>
              <div className="total">
                <span>Total</span>
                <span className="num">{moneyExact(totalCents)}</span>
              </div>
            </section>
            <Card title="Schedule">
              <dl className="prec-kv">
                <div>
                  <dt>{repeating ? "Start date" : "Date"}</dt>
                  <dd>{j.startDate ? shortDate(j.startDate) : "Not set"}</dd>
                </div>
                <div>
                  <dt>Arrival</dt>
                  <dd>{j.arriveFrom ? `Arrive ${timeRange(j.arriveFrom, j.arriveTo)}` : clock(j.startTime)}</dd>
                </div>
                <div>
                  <dt>Repeating</dt>
                  <dd>{j.frequencyLabel}</dd>
                </div>
                {repeating ? (
                  <div>
                    <dt>Ends</dt>
                    <dd>{j.endsOn ? shortDate(j.endsOn) : "Keeps going"}</dd>
                  </div>
                ) : null}
                {j.hours ? (
                  <div>
                    <dt>Takes</dt>
                    <dd>{minutesWords(j.hours * 60)}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Crew</dt>
                  <dd>
                    <CrewChips crew={j.crew} />
                  </dd>
                </div>
                {j.checklist ? (
                  <div>
                    <dt>Checklist</dt>
                    <dd>{j.checklist.name}</dd>
                  </div>
                ) : null}
              </dl>
            </Card>
            <div id="job-visits">{visitsCard}</div>
            <Card title="Overview" flush>
              <div className="prec-list">
                <div className="prec-row">
                  <span>
                    <b>{j.name}</b>
                  </span>
                </div>
                <div className="prec-row">
                  <span>
                    <span className="muted">Job number</span>
                    <b>{j.number ? `#${String(j.number)}` : "None yet"}</b>
                  </span>
                </div>
                <button
                  type="button"
                  className="prec-row"
                  onClick={() => void navigate({ to: "/clients/$id", params: { id: j.clientId } })}
                >
                  <Icon name="person" size={22} />
                  <span>
                    <b>{j.clientName}</b>
                    {j.address ? <span className="muted">{j.address}</span> : null}
                  </span>
                  <Icon name="chevronRight" size={20} />
                </button>
              </div>
            </Card>
            <Card title="Invoicing">
              <dl className="prec-kv">
                <div>
                  <dt>Type</dt>
                  <dd>{j.billing === "visit" ? "Visit based" : j.billing === "outside" ? "Not billed here" : "Monthly"}</dd>
                </div>
                <div>
                  <dt>Frequency</dt>
                  <dd>{billingWords}</dd>
                </div>
                <div>
                  <dt>Automatic payments</dt>
                  <dd>{j.autoCharge === "on" ? "On" : "Off"}</dd>
                </div>
                {j.card !== undefined ? (
                  <div>
                    <dt>Card on file</dt>
                    <dd>{cardLine(j.card, todayIn(company.timezone))}</dd>
                  </div>
                ) : null}
              </dl>
            </Card>
            {can("invoice.read") ? (
              <>
                <Card title="Invoices" flush>
                  {jobInvoices.isLoading ? (
                    <Skeleton rows={2} />
                  ) : invoiceRows.length ? (
                    <div className="prec-list">
                      {invoiceRows.map((i) => (
                        <button
                          key={i.id}
                          type="button"
                          className="prec-row"
                          onClick={() => void navigate({ to: "/invoices/$id", params: { id: i.id } })}
                        >
                          <Icon name="invoices" size={22} />
                          <span>
                            <b>{i.number ? `Invoice #${String(i.number)}` : "Invoice"}</b>
                            <span className="muted num">
                              {shortDate(i.issuedOn ?? i.firstServiceDate ?? "")} | {moneyExact(i.totalCents)}
                            </span>
                          </span>
                          <InvoiceStatus i={i} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="prec-empty">No invoices for this job yet.</p>
                  )}
                </Card>
                {paymentRows.length ? (
                  <Card title="Payments" flush>
                    <div className="prec-list">
                      {paymentRows.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="prec-row"
                          onClick={() => void navigate({ to: "/payments/$id", params: { id: p.id } })}
                        >
                          <span>
                            <b>Payment</b>
                            {p.tipCents > 0 ? <span className="muted num">Tip {moneyExact(p.tipCents)}</span> : null}
                          </span>
                          <span className="right num">-{moneyExact(p.amountCents)}</span>
                        </button>
                      ))}
                    </div>
                  </Card>
                ) : null}
              </>
            ) : null}
            {expensesCard}
            {timeCard}
            {laborCard}
            {profitCard}
          </>
        )}
      </PhoneRecord>
    );
  }

  return (
    <Page
      title={
        <>
          {j.name} <span className="muted num">#{j.number}</span>
        </>
      }
      titleMetaData={status}
      subtitle={
        <button type="button" className="link" onClick={() => void navigate({ to: "/clients/$id", params: { id: j.clientId } })}>
          {j.clientName}
        </button>
      }
      primaryAction={editBtn}
      secondaryAction={scheduleBtn}
      moreActions={moreMenu}
    >
      {banners}
      <div className="home-grid">
        <div className="stack">
          {linesCard}
          {visitsCard}
          {can("invoice.read") ? <JobBilling j={j} /> : null}
          {expensesCard}
          {timeCard}
          {laborCard}
          {notesCard}
        </div>
        <div className="stack">
          {detailsCard}
          {profitCard}
          {crewNotesCard}
        </div>
      </div>
    </Page>
  );
}

const oftenWords = (o: keyof typeof PRICE_OFTEN_WORDS) => PRICE_OFTEN_WORDS[o].toLowerCase();

/**
 * Over a job's line items: the home's bedrooms, bathrooms, other rooms and square feet (owner, 2026-11-02: the crew ask
 * for it so they know what is part of the job and do not over clean, and the office offers a repeat fast from it). The
 * client sees the same size on their quote and invoice. A home with no size saved shows what the job was priced for.
 */
function JobHome({
  job,
  prices,
  place,
  clientId,
  priced,
  editable,
  onPrice,
  offer,
}: {
  job: JobDetail;
  prices: PriceList | undefined;
  place: PropertyRow | null;
  clientId: string;
  priced: string | null;
  editable: boolean;
  onPrice: (() => void) | null;
  /** A one-time job: Offer a plan texts them a link to start their own schedule (owner, 2026-11-02). */
  offer: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [offering, setOffering] = useState(false);
  const size = place ? homeWords(place) : null;
  const words = size ?? priced;
  if (!place && !words) return null;
  return (
    <div className="lines-home">
      <span className="what">
        <span className="muted">Home </span>
        {words ? <b>{words}</b> : <span className="muted">No size yet. Add it so the crew know what is part of the job.</span>}
      </span>
      <span className="acts">
        {editable && place ? (
          <Button type="tertiary" size="small" onClick={() => setEditing(true)}>
            {size ? "Change" : "Add size"}
          </Button>
        ) : null}
        {onPrice ? (
          <Button type="tertiary" size="small" onClick={onPrice}>
            Price a repeat
          </Button>
        ) : null}
        {offer && words ? (
          <Button type="secondary" size="small" onClick={() => setOffering(true)}>
            Offer a plan
          </Button>
        ) : null}
      </span>
      {editing && place ? (
        <HomeSizeEdit place={place} clientId={clientId} job={job} prices={prices} onClose={() => setEditing(false)} />
      ) : null}
      {offering ? <PlanOfferSend clientId={clientId} onClose={() => setOffering(false)} /> : null}
    </div>
  );
}

/** The first line whose name holds a price list service ("Deep clean" is Deep clean), longest name first, with it. */
function handLine(job: JobDetail, list: PriceList): { line: LineRow; serviceId: string } | null {
  const services = [...list.services].sort((x, y) => y.name.length - x.name.length);
  for (const l of job.lines) {
    const d = l.description.toLowerCase();
    const s =
      services.find((x) => d.includes(x.name.toLowerCase())) ??
      services.find((x) => {
        const word = x.name.toLowerCase().split(/\s+/)[0] ?? "";
        return word.length > 3 && d.startsWith(word);
      });
    if (s) return { line: l, serviceId: s.id };
  }
  return null;
}

/**
 * The home's size, and the line priced from it (owner, 2026-11-02: "edit how many bedrooms & baths a line item should
 * have in case we let it slide after the cleaning was done"). The line takes the real size and keeps its price, so it
 * reads as changed by hand, and a plan is priced from the real size.
 */
function HomeSizeEdit({
  place,
  clientId,
  job,
  prices,
  onClose,
}: {
  place: PropertyRow;
  clientId: string;
  job: JobDetail;
  prices: PriceList | undefined;
  onClose: () => void;
}) {
  const { company } = useSession();
  const save = useSaveProperty();
  const update = useUpdateJob();
  const toast = useToast();
  const priced0 = job.lines.find((l) => l.answers);
  // A line priced by hand (an old website quote, or typed in) links to its service on the price list by name, so it
  // shows the real-size math, the plan prices under it and Let it pass too (owner, 2026-11-02).
  const hand = !priced0 && prices ? handLine(job, prices) : null;
  const line = priced0 ?? hand?.line ?? null;
  const base: HomeAnswers | null =
    priced0?.answers ??
    (hand
      ? {
          serviceId: hand.serviceId,
          sizeBy: "rooms",
          beds: 0,
          baths: 0,
          other: 0,
          sqft: 0,
          often: (PRICE_OFTEN as readonly string[]).includes(job.frequency) ? (job.frequency as HomeAnswers["often"]) : "once",
          extras: {},
        }
      : null);
  const serviceName = prices?.services.find((x) => x.id === base?.serviceId)?.name ?? "price list";
  // Let it pass (owner, 2026-11-02): keep this clean's price; the plan prices under the line come from the real size.
  const [letPass, setLetPass] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const start = (n: number | null) => (n === null ? "" : String(n));
  // A home with no size saved starts from what its line was priced for.
  const a = priced0?.answers;
  const blank = place.beds === null && place.baths === null && place.rooms === null && place.sqft === null;
  const [f, setF] = useState({
    beds: start(blank && a?.sizeBy === "rooms" ? a.beds || null : place.beds),
    baths: start(blank && a?.sizeBy === "rooms" ? a.baths || null : place.baths),
    rooms: start(blank && a?.sizeBy === "rooms" ? a.other || null : place.rooms),
    sqft: start(blank && a?.sizeBy === "sqft" ? a.sqft || null : place.sqft),
  });
  const n = (s: string) => (s.trim() && Number.isFinite(Number(s)) ? Number(s) : null);
  const count = (k: "beds" | "baths" | "rooms") => ({ value: f[k], onChange: (v: string) => setF({ ...f, [k]: v }) });
  const submit = async () => {
    setError(null);
    const sqft = n(f.sqft);
    try {
      await save.mutateAsync({
        clientId,
        id: place.id,
        property: {
          address: place.address,
          beds: n(f.beds),
          baths: n(f.baths),
          rooms: n(f.rooms),
          sqft: sqft === null ? null : Math.round(sqft),
        },
      });
      if (line && base) {
        // The priced line takes the real size and keeps its price (they let it slide); from today on, so visits
        // already done keep what they billed.
        const beds = n(f.beds) ?? 0;
        const baths = n(f.baths) ?? 0;
        const other = n(f.rooms) ?? 0;
        const feet = sqft === null ? 0 : Math.round(sqft);
        const bySqft = base.sizeBy === "sqft" ? feet > 0 : feet > 0 && beds + baths === 0;
        const sized: HomeAnswers = { ...base, sizeBy: bySqft ? "sqft" : "rooms", beds, baths, other, sqft: feet };
        // Not let pass: the line takes the price list's price for the real size (from today on).
        const priced = !letPass && prices ? priceMath(prices, sized) : null;
        const answers: HomeAnswers = { ...sized, letPass: letPass || undefined };
        const was = line.basis ? `, ${line.basis}` : null;
        const now = homeSize(answers, { sqft: bySqft ? feet : 0 });
        const lines: LineInput[] = job.lines.map((l) => ({
          description:
            l.id === line.id && was && l.description.endsWith(was)
              ? `${l.description.slice(0, -was.length)}, ${now}`
              : l.description,
          note: l.note,
          link: l.link ?? null,
          quantity: l.quantity,
          unitPriceCents: l.id === line.id && priced ? priced.cents : l.unitPriceCents,
          frequency: l.frequency as LineInput["frequency"],
          ...(l.id === line.id ? { answers } : l.answers ? { answers: l.answers } : {}),
        }));
        await update.mutateAsync({ id: job.id, patch: { lines, linesFrom: todayIn(company.timezone) } });
      }
      toast(
        !line || !base
          ? "Home size saved"
          : letPass
            ? "Home size saved. Same price, plans from the real size."
            : "Home size saved and priced",
      );
      onClose();
    } catch (err) {
      setError(errText(err));
    }
  };
  return (
    <Modal
      title="Home size"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending || update.isPending} onClick={() => void submit()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">The crew see this on the job and the visit. The client sees it on their quote and bill.</p>
        {line && base ? (
          <label className="row">
            <Switch checked={letPass} onChange={setLetPass} label={`Let it pass at ${moneyExact(line.unitPriceCents)}`} />
            <span className="small">
              {/* Plans never come from this line's service: they use the recurring prices (owner, 2026-11-02). */}
              {letPass
                ? `Let it pass. This clean stays ${moneyExact(line.unitPriceCents)} (${serviceName} price). Plans use your recurring prices for the real size.`
                : `Price this clean at the ${serviceName} price for the real size, from today on. Visits already done keep what they billed. Plans use your recurring prices.`}
            </span>
          </label>
        ) : null}
        {error ? <Banner type="error">{error}</Banner> : null}
        <div className="form-grid">
          <InputCount label="Bedrooms" max={20} {...count("beds")} />
          <InputCount label="Bathrooms" max={20} decimal {...count("baths")} />
          <InputCount label="Other rooms" max={50} {...count("rooms")} />
          <InputText
            label="Square feet"
            inputMode="numeric"
            autoComplete="off"
            value={f.sqft}
            onChange={(e) => setF({ ...f, sqft: e.target.value })}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * Under a job line priced from the price list: the home, the math and, for a one time price, what this home would cost to
 * repeat (owner, 2026-10-29: "on a line item that used autoquote for it, to underneath show how it got that, with it being
 * 3 beds, 2 baths, 2 other rooms etc").
 */
function PricedNote({ line, list }: { line: LineRow; list: PriceList | undefined }) {
  if (!line.answers || !list) return null;
  const m = priceMath(list, line.answers);
  if (!m) return <div className="muted small line-basis">Priced from the price list. That service is not on it anymore.</div>;
  const often = m.repeat ? `, ${oftenWords(m.repeat)}` : "";
  const hand =
    m.cents === line.unitPriceCents
      ? ""
      : line.answers.letPass
        ? ` Let it pass at ${moneyExact(line.unitPriceCents)}.`
        : ` Changed by hand to ${moneyExact(line.unitPriceCents)}.`;
  const last = m.repeats.length - 1;
  return (
    <div className="muted small line-basis">
      <div>
        {m.isMin
          ? `Auto quote for ${m.size}. The lowest price${often}, ${moneyExact(m.cents)}.`
          : `Auto quote for ${m.size}. ${m.math}${often} = ${moneyExact(m.cents)}.`}
        {hand}
      </div>
      {m.repeats.length ? (
        <div>
          To repeat,{" "}
          {m.repeats
            .map((r, i) => `${i && i === last ? "or " : ""}${moneyExact(r.perVisitCents)} ${oftenWords(r.often)}`)
            .join(", ")}
          . Open the line to make it repeat.
        </div>
      ) : null}
    </div>
  );
}

/** A price list add-on line ("Inside the fridge", "Inside windows × 3") or a first visit difference line, which pricing
 * again puts back from the new answers. */
const autoLine = (list: PriceList, d: string) =>
  d.endsWith(" on the first visit") || list.extras.some((e) => d === e.name || d.startsWith(`${e.name} × `));

/**
 * The auto quote again, from a job line priced by it: change a room, the service or how often, and the job takes the new
 * lines from today (or its first day, when that is ahead). A one-off made to repeat keeps its start day, so the next visit
 * is one round after it; when the first visit is still ahead it keeps the first visit price and add-ons on that visit.
 */
function RepriceLine({
  job,
  line,
  list,
  place,
  onClose,
}: {
  job: JobDetail;
  line: LineRow;
  list: PriceList;
  /** The home, whose size starts a line not priced from the list before. */
  place: PropertyRow | null;
  onClose: () => void;
}) {
  const { company } = useSession();
  const toast = useToast();
  const update = useUpdateJob();
  const visitLines = useVisitLines();
  const [error, setError] = useState<string | null>(null);
  const today = todayIn(company.timezone);
  const firstAhead = job.startDate !== null && job.startDate >= today;
  const from = job.startDate && job.startDate > today ? job.startDate : today;
  const use = async (quick: QuickQuote, answers: HomeAnswers) => {
    setError(null);
    const repeat = quick.frequency !== "one-time";
    const kept: LineInput[] = job.lines
      .filter((l) => l.id !== line.id && !l.answers && !autoLine(list, l.description))
      .map((l) => ({
        description: l.description,
        note: l.note,
        link: l.link ?? null,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        frequency: l.frequency as LineInput["frequency"],
      }));
    const asLine = (l: QuickQuote["lines"][number], first: boolean): LineInput => ({
      description: l.description,
      quantity: 1,
      unitPriceCents: l.unitPriceCents,
      note: l.note ?? null,
      link: l.link ?? null,
      frequency: "every",
      ...(first ? { answers } : {}),
    });
    const lines = [...quick.lines.filter((l) => !repeat || l.recurring).map((l, i) => asLine(l, i === 0)), ...kept];
    const changed = quick.frequency !== job.frequency;
    const patch: JobPatch = {
      lines,
      linesFrom: from,
      ...(changed
        ? {
            frequency: quick.frequency as JobPatch["frequency"] & string,
            // A done deep clean made to repeat is named for the repeat ("Cleaning every two weeks").
            title: firstAhead ? quick.title : (quick.lines[0]?.description.split(",")[0] ?? quick.title),
          }
        : {}),
    };
    try {
      await update.mutateAsync({ id: job.id, patch });
      if (repeat && firstAhead && job.startDate && quick.lines.some((l) => !l.recurring))
        await visitLines.mutateAsync({
          ref: `${job.id}.${job.startDate}`,
          lines: [...quick.lines.map((l, i) => asLine(l, i === 0)), ...kept],
          scope: "this",
        });
      toast(changed && repeat ? `Job repeats ${oftenWords(answers.often)} now.` : "Price updated.");
      onClose();
    } catch (e) {
      setError(errText(e));
    }
  };
  return (
    <Modal title="Price it again" open onClose={onClose}>
      <div className="stack">
        <p className="muted small">
          {job.frequency === "one-time"
            ? "Change the rooms or the service, or pick how often to make it a repeat job. Visits after the first get the repeat price."
            : "Change the rooms, the service or how often."}{" "}
          {from === today ? `It starts today. Visits before keep their price.` : `It starts ${shortDate(from)}.`}
        </p>
        {error ? <Banner type="error">{error}</Banner> : null}
        <QuickPrice
          start={line.answers ?? undefined}
          size={
            line.answers || !place ? undefined : { beds: place.beds, baths: place.baths, rooms: place.rooms, sqft: place.sqft }
          }
          useLabel={update.isPending || visitLines.isPending ? "Saving" : "Update the job"}
          onUse={(q, a) => void use(q, a)}
        />
      </div>
    </Modal>
  );
}

function JobVisits({ job, today }: { job: JobDetail; today: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const visits = useVisits(
    company.id,
    { from: addDays(today, -60), to: addDays(today, 90), jobId: job.id },
    can("schedule.read"),
  );
  if (!can("schedule.read")) return null;
  const [fixing, setFixing] = useState(false);
  const repeating = job.frequency !== "one-time";
  const all = visits.data?.visits ?? [];
  // A complaint about a done one-time job: the crew goes back for free (api visits/fix.ts).
  const fixFor = repeating ? undefined : all.filter((v) => v.status === "done" && !v.fix).at(-1);
  const upcoming = all.filter((v) => v.date >= today && v.status !== "done").slice(0, 6);
  const recent = all
    .filter((v) => v.date < today || v.status === "done")
    .reverse()
    .slice(0, 4);
  const row = (v: (typeof all)[number]) => (
    <button
      type="button"
      className="item visit-row"
      key={v.ref}
      onClick={() => void navigate({ to: "/visits/$ref", params: { ref: v.ref } })}
    >
      <span className="when">
        <b>{dayWords(v.date, today)}</b>
        <span className="muted num">{clock(v.startTime)}</span>
      </span>
      <div className="what">
        <b>{daysAway(v.date, today)}</b>
        <span>{v.fix ? "Fix visit, no charge" : v.moved ? `Moved from ${shortDate(v.originalDate)}` : money(v.totalCents)}</span>
      </div>
      <CrewChips crew={v.crew} />
      <VisitStatus status={v.status} late={v.date < today} />
    </button>
  );
  return (
    <Card
      title={repeating ? "Scheduled visits" : "Visits"}
      flush
      action={
        repeating && can("job.update") ? (
          <Button type="secondary" size="small" onClick={() => void navigate({ to: "/jobs/$id/edit", params: { id: job.id } })}>
            Edit all visits
          </Button>
        ) : fixFor && can("schedule.update") ? (
          <Button type="secondary" size="small" onClick={() => setFixing(true)}>
            Add fix visit
          </Button>
        ) : undefined
      }
    >
      {fixing && fixFor ? (
        <FixVisit
          job={job}
          fixFor={fixFor}
          today={today}
          onClose={() => setFixing(false)}
          onDone={(ref) => void navigate({ to: "/visits/$ref", params: { ref } })}
        />
      ) : null}
      {repeating ? (
        <dl className="visit-sum">
          <div>
            <dt>First visit</dt>
            <dd>{job.startDate ? shortDate(job.startDate) : "Not set"}</dd>
          </div>
          <div>
            <dt>Last visit</dt>
            <dd>{job.endsOn ? shortDate(job.endsOn) : "Keeps going"}</dd>
          </div>
          <div>
            <dt>Repeats</dt>
            <dd>{job.frequencyLabel}</dd>
          </div>
          <div>
            <dt>Checklist</dt>
            <dd>{job.checklist?.name ?? "None"}</dd>
          </div>
        </dl>
      ) : null}
      {visits.isLoading ? (
        <Skeleton rows={3} />
      ) : (
        <>
          <h3 className="card-sub">Coming up</h3>
          <div className="todo">
            {upcoming.length ? upcoming.map(row) : <div className="card-empty muted">Nothing planned.</div>}
          </div>
          <h3 className="card-sub">Recent</h3>
          <div className="todo">{recent.length ? recent.map(row) : <div className="card-empty muted">No visits yet.</div>}</div>
        </>
      )}
    </Card>
  );
}

/**
 * Add fix visit (owner, Oct 31): the client complained about a done one-time job, so the crew goes back. It is a visit of
 * the same job with no charge; it goes on the same invoice when done, and no automatic text goes out for it.
 */
function FixVisit({
  job,
  fixFor,
  today,
  onClose,
  onDone,
}: {
  job: JobDetail;
  fixFor: VisitCard;
  today: string;
  onClose: () => void;
  onDone: (ref: string) => void;
}) {
  const { company } = useSession();
  const toast = useToast();
  const people = usePeople(company.id);
  const fix = useVisitFix();
  const team = (people.data ?? []).filter((p) => p.active);
  const [date, setDate] = useState(addDays(today, 1));
  const [time, setTime] = useState(fixFor.startTime ?? job.startTime ?? "09:00");
  const [crew, setCrew] = useState<Set<string>>(new Set(fixFor.crew.map((c) => c.id)));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = () =>
    fix.mutate(
      { ref: fixFor.ref, date, startTime: time || null, crew: [...crew], crewNotes: note.trim() || null },
      {
        onSuccess: (d) => {
          toast(`Fix visit added for ${shortDate(d.date)}`);
          onClose();
          onDone(d.ref);
        },
        onError: (e) => setError(errText(e)),
      },
    );
  return (
    <Modal
      title="Add fix visit"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!date} loading={fix.isPending} onClick={save}>
            Add the visit
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          For a complaint about the {shortDate(fixFor.date)} visit. There is no charge. It goes on the same invoice when it is
          done, and the client gets no automatic text.
        </p>
        {error ? <Banner type="error">{error}</Banner> : null}
        <div className="row">
          <InputText label="Day" type="date" min={fixFor.date} value={date} onChange={(e) => setDate(e.target.value)} />
          <InputText label="Start time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        <FormField label="Who goes">
          <div className="pick-list">
            {team.map((p) => (
              <label key={p.id} className="pick">
                <input
                  type="checkbox"
                  checked={crew.has(p.id)}
                  onChange={() => {
                    const n = new Set(crew);
                    if (n.has(p.id)) n.delete(p.id);
                    else n.add(p.id);
                    setCrew(n);
                  }}
                />
                <b>{p.name}</b>
              </label>
            ))}
          </div>
        </FormField>
        <InputText
          label="What to fix"
          description="The crew sees this on the visit."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// Form ---------------------------------------------------------------------------------------------------------------

/**
 * Confirm for them (owner, Oct 31): a client who cannot use the link (older, no smartphone) confirms on the phone. The office
 * takes their card by phone if none is on file, then confirms. The job keeps who confirmed and when (confirmedByHand).
 */
function ConfirmForThem({ job, onClose }: { job: JobDetail; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdateJob();
  const client = useClient(job.clientId);
  const [taking, setTaking] = useState(false);
  const c = client.data;
  if (taking && c) return <PhoneCard c={c} onClose={() => setTaking(false)} onSaved={() => setTaking(false)} />;
  const confirm = () =>
    update.mutate(
      { id: job.id, patch: { active: true } },
      {
        onSuccess: () => {
          toast(`Confirmed for ${job.clientName}`);
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Modal
      title={`Confirm for ${job.clientName}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={update.isPending} disabled={!c} onClick={confirm}>
            Confirm the job
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          Use this when they said yes on the phone and cannot sign the link. No signature is needed. We save your name and the
          time as the OK.
        </p>
        {!c ? (
          <Skeleton rows={2} />
        ) : c.card ? (
          <Banner type="success">
            Card on file, {c.card.brand} ending in {c.card.last4}.
          </Banner>
        ) : (
          <Banner
            type="warning"
            action={
              <Button size="small" type="secondary" onClick={() => setTaking(true)}>
                Take card by phone
              </Button>
            }
          >
            No card on file yet.
          </Banner>
        )}
      </div>
    </Modal>
  );
}

export function JobForm({ id, clientId, quoteId }: { id?: string; clientId?: string; quoteId?: string }) {
  const existing = useJob(id ?? "");
  const quote = useQuote(quoteId ?? "");
  if (id && !existing.data)
    return <Page title="Edit job">{existing.error ? <LoadError error={existing.error} /> : <Skeleton rows={6} />}</Page>;
  if (quoteId && !quote.data)
    return <Page title="New job">{quote.error ? <LoadError error={quote.error} /> : <Skeleton rows={6} />}</Page>;
  if (quote.data && quote.data.status !== "approved")
    return (
      <Page title="New job" subtitle={`Quote #${String(quote.data.number)}`}>
        <Banner type="warning">
          {quote.data.status === "converted"
            ? "This quote is already a job."
            : "Only an approved quote can become a job. The client approves it first."}
        </Banner>
      </Page>
    );
  return <JobFormBody j={id ? existing.data : undefined} clientId={clientId} q={quote.data} />;
}

/** Make into a job: the quote's approved lines, picked extras included. A repeat's first visit only lines stay on the first visit. */
function quoteLines(q: QuoteDetail, repeats: boolean): DraftLine[] {
  return q.lines
    .filter((l) => !l.optional || l.picked)
    .map((l) =>
      draftLine({
        description: l.description,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        note: l.note,
        link: l.link ?? null,
        frequency: repeats && !l.recurring ? "once" : "every",
      }),
    );
}

/**
 * New job and Edit job, in the order a call goes (owner, 2026-10-30: "like a quote with fast adding"): who it is for, the
 * price (Price it fast fills the lines), the work and how often, when, the crew, the office note. A new client confirms
 * first: they get a link to save a card and confirm, and the job goes on the schedule once they do (api jobs/confirm.ts).
 */
function JobFormBody({
  j,
  clientId,
  q,
}: {
  j: JobDetail | undefined;
  clientId: string | undefined;
  /** Make into a job on a quote: the form opens filled from it, and saving marks the quote a job. */
  q?: QuoteDetail | undefined;
}) {
  const { company } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const create = useCreateJob();
  const update = useUpdateJob();
  const today = todayIn(company.timezone);
  const clients = useClients(company.id, { status: "active", sort: "last_activity" }, !j);
  const people = usePeople(company.id);
  const checklists = useChecklists(company.id);
  const [client, setClient] = useState(j?.clientId ?? q?.clientId ?? clientId ?? "");
  const picked = useClient(client);
  const [newClient, setNewClient] = useState<NewClientStart | null>(null);
  const [madeHere, setMadeHere] = useState<string | null>(null);
  const quoteOften = q && (FREQUENCIES as readonly string[]).includes(q.frequency) ? q.frequency : null;
  const [f, setF] = useState({
    title: j?.title ?? q?.title ?? "",
    service: j?.service ?? "",
    startDate: j?.startDate ?? addDays(today, 1),
    startTime: j?.startTime ?? "09:00",
    hours: j?.hours ? String(j.hours) : "",
    endsOn: j?.endsOn ?? "",
    propertyId: j?.propertyId ?? "",
    billing: (j?.billing ?? "visit") as BillingKind,
    officeNotes: j?.officeNotes ?? "",
    crewNotes: j?.crewNotes ?? "",
    crewNotesEs: j?.crewNotesEs ?? "",
    checklistId: j?.checklist?.id ?? "",
  });
  const [repeat, setRepeat] = useState<RepeatValue>(
    j
      ? { frequency: j.frequency, repeatDay: j.repeatDay, repeatRule: j.repeatRule }
      : { frequency: quoteOften ?? "weekly", repeatDay: null, repeatRule: null },
  );
  const [keepsGoing, setKeepsGoing] = useState(!j?.endsOn);
  const [crew, setCrew] = useState<Set<string>>(new Set(j?.crew.map((c) => c.id) ?? []));
  const [lines, setLines] = useState<DraftLine[]>(() => {
    if (j?.lines.length) return j.lines.map(draftLine);
    const fromQuote = q ? quoteLines(q, (quoteOften ?? "weekly") !== "one-time") : [];
    return fromQuote.length ? fromQuote : [draftLine()];
  });
  const [linesTouched, setLinesTouched] = useState(false);
  const [linesFrom, setLinesFrom] = useState(today);
  // The client signed the quote, so no confirm link unless the office turns it on.
  const [confirm, setConfirm] = useState<boolean | null>(q ? false : null);
  const [error, setError] = useState<string | null>(null);
  const repeating = repeat.frequency !== "one-time";
  // Switching how often a started job runs: the new repeat counts from the last visit done (Every 2 weeks to Monthly with
  // the last cut Oct 24 is Nov 21), not the start date. The office can pick another next visit.
  const started = Boolean(j?.startDate && j.startDate < today && f.startDate === j.startDate);
  const was: RepeatValue | null = j ? { frequency: j.frequency, repeatDay: j.repeatDay, repeatRule: j.repeatRule } : null;
  const switching = started && was !== null && repeating && JSON.stringify(repeat) !== JSON.stringify(was);
  const [nextOn, setNextOn] = useState<string | null>(null);
  const pickRepeat = (v: RepeatValue) => {
    if (!started || !j || v.frequency === "one-time") {
      setRepeat(v);
      return;
    }
    const d = nextAfterSwitch(v, j.lastDone, today, was ?? undefined);
    setNextOn(d);
    setRepeat(moveRepeat(v, d));
  };
  // The Repeats list is named from the day the repeat counts from (Weekly on Mondays).
  const repeatDate =
    switching && nextOn
      ? nextOn
      : (firstRepeat({ ...repeat, startDate: f.startDate, repeatFrom: j?.repeatFrom ?? null }) ?? f.startDate);
  const set = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  const team = (people.data ?? []).filter((p) => p.active);
  const who = picked.data?.id === client ? picked.data : undefined;
  // Every client confirms (owner, 2026-10-30: they OK it on the phone, then say they never did). A card on file means sign only.
  // A client who already signed a quote for it does not: they get the usual Job scheduled text.
  const signed = Boolean(who?.signedQuote);
  const askConfirm = !j && Boolean(who) && (confirm ?? !signed);
  const hasCard = Boolean(who?.card) && madeHere !== who?.id;
  const place = who?.properties.find((p) => p.id === f.propertyId) ?? who?.properties[0];
  const size = place ? { beds: place.beds, baths: place.baths, rooms: place.rooms, sqft: place.sqft } : undefined;
  const applyQuick = (quick: QuickQuote, answers: HomeAnswers | null) => {
    const frequency = (FREQUENCIES as readonly string[]).includes(quick.frequency) ? quick.frequency : repeat.frequency;
    const repeats = frequency !== "one-time";
    setF({ ...f, title: quick.title });
    if (frequency !== repeat.frequency) setRepeat({ frequency, repeatDay: null, repeatRule: null });
    // A repeat's first visit lines (the deep clean, the add-ons) go on the first visit only.
    setLines(
      quick.lines.map((l, i) =>
        draftLine({
          description: l.description,
          quantity: 1,
          unitPriceCents: l.unitPriceCents,
          note: l.note ?? null,
          link: l.link ?? null,
          frequency: repeats && !l.recurring ? "once" : "every",
          answers: i === 0 ? answers : null,
        }),
      ),
    );
    setLinesTouched(true);
    setError(null);
    toast("Price is on the job");
  };
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const out = linesOut(lines);
    if (typeof out === "string") {
      setError(out);
      return;
    }
    if (!client && !q) {
      setError("Pick the client.");
      return;
    }
    if (repeating && !keepsGoing && !f.endsOn) {
      setError("Pick the end date, or pick Keeps going.");
      return;
    }
    if (repeating && !keepsGoing && f.endsOn < f.startDate) {
      setError("The end date is before the start.");
      return;
    }
    if (askConfirm && !out.length) {
      setError("Add the price so the client can see what they confirm.");
      return;
    }
    const hours = f.hours.trim() ? Number(f.hours) : null;
    const body = {
      title: f.title.trim() || undefined,
      service: f.service.trim() || undefined,
      frequency: repeat.frequency as (typeof FREQUENCIES)[number],
      startDate: f.startDate,
      repeatDay: repeat.repeatDay,
      repeatRule: repeat.repeatRule,
      startTime: f.startTime || null,
      hours: hours !== null && Number.isFinite(hours) ? hours : null,
      endsOn: repeating && !keepsGoing && f.endsOn ? f.endsOn : null,
      propertyId: f.propertyId || null,
      crew: [...crew],
      billing: f.billing,
      officeNotes: f.officeNotes.trim() || undefined,
      crewNotes: f.crewNotes.trim() || undefined,
      crewNotesEs: f.crewNotesEs.trim() || undefined,
      checklistId: f.checklistId || null,
    };
    const done = (x: JobDetail) => {
      if (x.confirm)
        toast(
          x.confirm.sentTo
            ? `Job #${String(x.number ?? "")} made. Confirm link sent to ${x.confirm.sentTo}.`
            : `Job #${String(x.number ?? "")} made. Send the confirm link from the job.`,
        );
      else toast(j ? "Job saved" : `Job #${String(x.number ?? "")} made`);
      if (!j && !x.confirm) markFresh(`job:${x.id}`);
      if (q) void qc.invalidateQueries({ queryKey: ["quote"] });
      void navigate({ to: "/jobs/$id", params: { id: x.id } });
    };
    const fail = (er: unknown) => setError(errText(er));
    if (j)
      update.mutate(
        {
          id: j.id,
          patch: {
            ...body,
            ...(switching && nextOn ? { repeatFrom: nextOn } : {}),
            ...(linesTouched ? { lines: out, linesFrom: repeating ? linesFrom : (j.startDate ?? today) } : {}),
          },
        },
        { onSuccess: done, onError: fail },
      );
    else
      create.mutate(
        { ...body, ...(client ? { clientId: client } : {}), ...(q ? { quoteId: q.id } : {}), lines: out, askConfirm },
        { onSuccess: done, onError: fail },
      );
  };
  const pickedRow = (clients.data?.items ?? []).find((c) => c.id === client);
  return (
    <Page
      title={j ? `Edit job #${String(j.number ?? "")}` : "New job"}
      subtitle={j?.clientName ?? (q ? `From quote #${String(q.number)}` : undefined)}
    >
      <form className="stack pform" onSubmit={submit}>
        {error ? <Banner type="error">{error}</Banner> : null}
        {j ? null : (
          <Card title="Client">
            <div className="stack">
              {q ? (
                <div>
                  <b>{who?.name ?? q.contactName ?? q.name}</b>
                  <p className="muted small">
                    {q.clientId
                      ? [q.address ?? who?.address, phoneText(who?.phone ?? q.contactPhone)].filter(Boolean).join(" · ")
                      : `${[q.address, phoneText(q.contactPhone)].filter(Boolean).join(" · ")}${q.address || q.contactPhone ? ". " : ""}They become a client when you make the job.`}
                  </p>
                </div>
              ) : (
                <Combobox
                  label="Client"
                  value={client}
                  onChange={(v) => {
                    if (v === NEW_CLIENT) return;
                    setClient(v);
                    setConfirm(null);
                    setF({ ...f, propertyId: "" });
                  }}
                  options={(clients.data?.items ?? []).map((c) => ({
                    value: c.id,
                    label: c.name,
                    sub: [c.address, [c.email, phoneText(c.phone)].filter(Boolean).join(" · ")].filter(Boolean).join("\n"),
                  }))}
                  last={{
                    value: NEW_CLIENT,
                    label: "+ Create new client",
                    onPick: (typed) => {
                      const [first = "", ...rest] = typed.split(/\s+/);
                      setNewClient({ firstName: first, lastName: rest.join(" ") });
                    },
                  }}
                />
              )}
              {(who?.properties.length ?? 0) > 1 ? (
                <Combobox
                  label="Where"
                  value={f.propertyId}
                  onChange={(v) => setF({ ...f, propertyId: v })}
                  options={(who?.properties ?? []).map((p) => ({
                    value: p.id,
                    label: p.label ? `${p.label} · ${p.address}` : p.address,
                  }))}
                />
              ) : !q && (who || pickedRow) ? (
                <p className="muted small">
                  {[pickedRow?.address ?? who?.address, phoneText(pickedRow?.phone ?? who?.phone ?? null)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}
            </div>
          </Card>
        )}
        {j || q ? null : company.trade === "cleaning" ? (
          <QuickPrice
            size={size}
            useLabel="Put it on the job"
            planStart={who?.planStart}
            onUse={(q, a) => applyQuick(q, a)}
            onHourly={(q) => applyQuick(q, null)}
          />
        ) : company.trade === "lawn" ? (
          <LawnQuickPrice
            sqft={place?.lawnSqft}
            address={place?.address ?? who?.address ?? null}
            useLabel="Put it on the job"
            onUse={(q) => applyQuick(q, null)}
          />
        ) : null}
        <Card title="The work">
          <div className="stack">
            <div className="form-grid">
              <InputText
                label="Job title"
                placeholder="Weekly House Cleaning"
                description="Leave it blank to use the service."
                {...set("title")}
              />
              <InputText label="Service" placeholder="House Cleaning" {...set("service")} />
            </div>
            <RepeatPicker value={repeat} date={repeatDate} onChange={pickRepeat} />
            {switching && nextOn ? (
              <InputText
                label="Next visit"
                type="date"
                value={nextOn}
                min={today}
                onChange={(e) => {
                  const d = e.target.value;
                  if (!d) return;
                  setNextOn(d);
                  setRepeat(moveRepeat(repeat, d));
                }}
                description={
                  j?.lastDone
                    ? `Counted from the last visit on ${shortDate(j.lastDone)}. Visits before today stay where they are.`
                    : "No visit done yet, so it goes on today. Visits before today stay where they are."
                }
              />
            ) : null}
            <LinesEditor
              lines={lines}
              repeats={repeating}
              onChange={(l) => {
                setLines(l);
                setLinesTouched(true);
              }}
            />
            {j && repeating && linesTouched ? (
              <InputText
                label="New prices start on"
                type="date"
                value={linesFrom}
                min={today}
                onChange={(e) => setLinesFrom(e.target.value)}
                description="Visits before this keep the old prices."
              />
            ) : null}
            <Choice
              label="Billing"
              value={f.billing}
              onChange={(v) => setF({ ...f, billing: v })}
              options={[
                { value: "visit", label: "After each visit" },
                { value: "monthly", label: "Once a month" },
                { value: "monthend", label: "At month end" },
                { value: "outside", label: "Not billed here" },
              ]}
            />
          </div>
        </Card>
        <Card title="When">
          <div className="stack">
            <div className="form-grid">
              <InputText label={repeating ? "First visit" : "Date"} type="date" {...set("startDate")} />
              <InputText label="Start time" type="time" {...set("startTime")} />
              <InputText label="How long" suffix="hours" inputMode="decimal" placeholder="3" {...set("hours")} />
            </div>
            {repeating && repeat.frequency !== "as-needed" ? (
              <div className="form-grid">
                <Choice
                  label="Ends"
                  value={keepsGoing ? "never" : "on"}
                  onChange={(v) => setKeepsGoing(v === "never")}
                  options={[
                    { value: "never", label: "Keeps going", hint: "No end date" },
                    { value: "on", label: "Ends on a day" },
                  ]}
                />
                {keepsGoing ? null : <InputText label="Ends on" type="date" min={f.startDate} {...set("endsOn")} />}
              </div>
            ) : null}
          </div>
        </Card>
        <Card title="Crew">
          <div className="stack">
            <FormField label="Who goes" description={crew.size ? `${String(crew.size)} picked` : "You can pick the crew later."}>
              <div className="pick-list">
                {team.map((p) => (
                  <label key={p.id} className="pick">
                    <input
                      type="checkbox"
                      checked={crew.has(p.id)}
                      onChange={() => {
                        const n = new Set(crew);
                        if (n.has(p.id)) n.delete(p.id);
                        else n.add(p.id);
                        setCrew(n);
                      }}
                    />
                    <b>{p.name}</b>
                    <span className="muted">{p.role === "crew" ? "" : p.role}</span>
                  </label>
                ))}
              </div>
            </FormField>
            {checklists.data?.length ? (
              <Combobox
                label="Checklist"
                value={f.checklistId}
                onChange={(v) => setF({ ...f, checklistId: v })}
                options={[{ value: "", label: "No checklist" }, ...checklists.data.map((c) => ({ value: c.id, label: c.name }))]}
              />
            ) : null}
            <FormField label="Notes for the crew">
              <textarea
                rows={3}
                value={f.crewNotes}
                onChange={(e) => setF({ ...f, crewNotes: e.target.value })}
                placeholder="Dog is friendly. Start upstairs."
              />
            </FormField>
            <FormField label="Notes for the crew in Spanish" description="Crew who use the app in Spanish see these.">
              <textarea
                rows={3}
                value={f.crewNotesEs}
                onChange={(e) => setF({ ...f, crewNotesEs: e.target.value })}
                placeholder="El perro es amigable."
              />
            </FormField>
          </div>
        </Card>
        <Card title="Office">
          <FormField label="Office note" description="The crew does not see it.">
            <textarea rows={2} value={f.officeNotes} onChange={(e) => setF({ ...f, officeNotes: e.target.value })} />
          </FormField>
        </Card>
        {!j && who ? (
          <Card title="Confirm with the client">
            <label className="row">
              <Switch checked={askConfirm} onChange={setConfirm} label="Client confirms and signs" />
              <span>
                <b>Client confirms and signs</b>
                <span className="muted small" style={{ display: "block" }}>
                  {askConfirm
                    ? `We text ${who.firstName ?? who.name} a link to confirm the place and day. ${hasCard ? "They sign. Their card is on file." : "They save a card and sign."} The job goes on the schedule now, marked Not confirmed until they do.`
                    : signed
                      ? "They already signed a quote. The job goes on the schedule now and they get the usual booked text."
                      : "The job goes on the schedule now. No confirm link."}
                </span>
              </span>
            </label>
          </Card>
        ) : null}
        <div className="row end form-bar">
          <Button type="tertiary" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <Button submit loading={create.isPending || update.isPending}>
            {j ? "Save job" : askConfirm ? "Make job and send link" : "Make job"}
          </Button>
        </div>
        {!j && repeating ? (
          <p className="muted small">
            <Icon name="repeat" size={14} /> Visits fill the schedule from the first visit on. You can move or skip any of them.
          </p>
        ) : null}
      </form>
      <NewClientBox
        start={newClient}
        trade={company.trade}
        onClose={() => setNewClient(null)}
        onMade={(c) => {
          setNewClient(null);
          setClient(c.id);
          setMadeHere(c.id);
          setConfirm(null);
          setF({ ...f, propertyId: "" });
          setError(null);
        }}
      />
    </Page>
  );
}
